import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const Collections = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const [activeFilter, setActiveFilter] = useState('all');

  const collections = [
    {
      id: 1,
      name: 'Marble Finish',
      category: 'marble',
      image: 'https://images.pexels.com/photos/7718458/pexels-photo-7718458.jpeg?_gl=1*1mw99v6*_ga*MTEwNzAyNjQ3LjE3NzE0MDQ5OTY.*_ga_8JE65Q40S6*czE3NzE0OTEwMjckbzMkZzEkdDE3NzE0OTEzMTAkajMzJGwwJGgw',
      size: 'large',
    },
    {
      id: 2,
      name: 'Moroccon Texture',
      category: 'moroccon',
      image: 'https://images.pexels.com/photos/7911707/pexels-photo-7911707.jpeg?_gl=1*ushr4a*_ga*ODcwOTIwNDU1LjE3NzE5MTUxMDk.*_ga_8JE65Q40S6*czE3NzE5MTUxMDgkbzEkZzEkdDE3NzE5MTUxMjUkajQzJGwwJGgw',
      size: 'normal',
    },
    {
      id: 3,
      name: 'Contemporary Minimal',
      category: 'minimal',
      image: 'https://images.pexels.com/photos/28408521/pexels-photo-28408521.jpeg?_gl=1*1w8wh0y*_ga*MTEwNzAyNjQ3LjE3NzE0MDQ5OTY.*_ga_8JE65Q40S6*czE3NzE0OTEwMjckbzMkZzEkdDE3NzE0OTE5ODkkajU0JGwwJGgw',
      size: 'normal',
    },
    {
      id: 4,
      name: 'Large Format Slabs',
      category: 'slabs',
      image: 'https://bluecerallp.com/uploads/pages/big%20slab.webp',
      size: 'large',
    },
    {
      id: 5,
      name: 'Outdoor & Elevation',
      category: 'outdoor',
      image: 'https://images.unsplash.com/photo-1767022062386-36d4af393776?q=80&w=800&auto=format&fit=crop',
      size: 'normal',
    },
  ];

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'marble', label: 'Marble' },
    { id: 'moroccon', label: 'Moroccon' },
    { id: 'minimal', label: 'Minimal' },
    { id: 'slabs', label: 'Slabs' },
    { id: 'outdoor', label: 'Outdoor' },
  ];

  const filteredCollections = activeFilter === 'all'
    ? collections
    : collections.filter(c => c.category === activeFilter);

  return (
    <section
      id="collections"
      ref={sectionRef}
      data-testid="collections-section"
      className="py-24 md:py-32 lg:py-40 bg-stone-white"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-burgundy font-medium">
            Our Work
          </span>
          <h2
            data-testid="collections-heading"
            className="font-playfair text-3xl md:text-4xl lg:text-5xl text-matte-charcoal mt-4"
          >
            Explore Our Tiles Collection
          </h2>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              data-testid={`filter-${filter.id}`}
              className={`px-5 py-2 text-xs uppercase tracking-[0.15em] font-medium transition-all duration-300 ${
                activeFilter === filter.id
                  ? 'bg-burgundy text-white'
                  : 'bg-transparent text-stone-grey hover:text-matte-charcoal border border-sand hover:border-burgundy/30'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </motion.div>

        {/* Collections Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCollections.map((collection, index) => (
            <motion.div
              key={collection.id}
              data-testid={`collection-card-${collection.id}`}
              className={`group relative overflow-hidden cursor-pointer ${
                collection.size === 'large' ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="aspect-[4/5] relative overflow-hidden">
                <img
                  src={collection.image}
                  alt={collection.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-matte-charcoal/80 via-matte-charcoal/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <h3 className="font-playfair text-xl md:text-2xl text-white mb-2 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    {collection.name}
                  </h3>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 delay-100">
                    {/* <span className="text-xs uppercase tracking-[0.15em] text-white/80">
                      View Collection
                    </span> */}
                    {/* <svg
                      className="w-4 h-4 text-white/80"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg> */}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Collections;
